"use client";

import React, { useState, useEffect, useRef } from "react";
import { Button, Typography } from "@shared/ui.primitives";
import {
  SignupType,
  trackSegmentNewsLetterSignup
} from "data/tracking/segment/track";
import { NewsletterSignupLocationType } from "data/tracking/segment/helpers";
import { useLocale } from "hooks";
import { ArrowRight, CloseIcon } from "components/SVG";
import { color } from "lib/theme/theme.css";

import { registerContact } from "./Newsletter.utils";
import {
  footerNewsletterInput,
  footerNewsletterInputArrow,
  hidden,
  newsletterFormWrapper,
  newsletterInputGroup,
  newsletterModalContent,
  newsletterModalHeader,
  newsletterModalWrapper,
  newsletterOverlay,
  newsletterOverlayDisplay,
  newsletterWrapper
} from "./Newsletter.css";

type Props = {
  placeholder?: string;
  confirmationHeading: string;
  confirmationCopy: string;
  errorHeading: string;
  errorCopy: string;
  onSuccess?: () => void;
  onError?: () => void;
  source?: string;
  layout: "mobile" | "desktop";
};

export const Newsletter: React.FC<Props> = ({
  placeholder = "Enter your email address here",
  confirmationHeading,
  confirmationCopy,
  errorHeading,
  errorCopy,
  onSuccess,
  onError,
  layout,
  source = "Email Footer"
}) => {
  const [emailAddress, setEmailAddress] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const locale = useLocale();
  const country = locale === "en-US" ? "US" : "UK";
  const dialogHeading = isError ? errorHeading : confirmationHeading;
  const dialogCopy = isError ? errorCopy : confirmationCopy;
  const continueShoppingButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && continueShoppingButtonRef.current) {
      continueShoppingButtonRef.current.focus();
    }
  }, [isOpen]);

  const handleCallback = (result: boolean): void => {
    if (result && onSuccess) onSuccess();
    if (!result && onError) onError();
    setIsError(!result);
    setIsOpen(true);
  };

  const subscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await registerContact({
        source,
        email: emailAddress,
        country
      });
      if (result) setEmailAddress("");
      trackSegmentNewsLetterSignup(
        SignupType.Newsletter,
        NewsletterSignupLocationType.Footer
      );
      handleCallback(result);
    } catch {
      handleCallback(false);
    }
  };

  return (
    <>
      <button
        type="button"
        id="overlay"
        className={
          isOpen
            ? `${newsletterOverlay} ${newsletterOverlayDisplay}`
            : newsletterOverlay
        }
        onClick={() => setIsOpen(false)}
        onKeyPress={() => setIsOpen(false)}
        tabIndex={0}
        aria-label="Close modal"
      />
      <div className={newsletterWrapper} aria-labelledby={dialogHeading}>
        {!onSuccess && (
          <dialog className={isOpen ? newsletterModalWrapper : hidden}>
            <div className={newsletterModalContent}>
              <div className={newsletterModalHeader}>
                <Typography as="h3" variant="subheader-3" colour="primary-dark">
                  {dialogHeading}
                </Typography>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close"
                >
                  <CloseIcon width="20px" />
                </button>
              </div>

              <Typography as="p" variant="body-md" colour="primary-dark">
                {dialogCopy}
              </Typography>

              <Button
                text="Continue Shopping"
                type="button"
                variant="primary"
                size="medium"
                width="full"
                onClick={() => setIsOpen(false)}
                dataTestId="ModalButtonContinue"
                ref={continueShoppingButtonRef}
              />
            </div>
          </dialog>
        )}

        <div className={newsletterFormWrapper}>
          <form
            onSubmit={subscribe}
            data-testid="NewsletterForm"
            id={`${layout}-newsletterSignUp`}
          >
            <div
              className={newsletterInputGroup}
              data-testid="NewsletterFormInputGroup"
            >
              <input
                required
                type="email"
                className={footerNewsletterInput}
                value={emailAddress}
                placeholder={placeholder}
                onChange={(e) => setEmailAddress(e.target.value)}
                data-testid="NewsletterFormInput"
              />
              <button
                type="submit"
                aria-label="Submit your email address"
                data-testid="NewsletterSubmit"
                className={footerNewsletterInputArrow}
              >
                <ArrowRight color={color.contentTertiary} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
